<template>
    <div
        ref="rootRef"
        class="tw:relative"
        :data-current-teatro="current && current.id ? current.id : ''"
        data-testid="header-teatro-selector"
    >
        <!-- Mientras carga se pinta el propio botón (sin skeleton): el
             skeleton vive dentro del panel, que es donde se listan los teatros. -->
        <template v-if="loading || hasOptions">
            <button
                type="button"
                class="admin-teatro-toggle tw:group tw:relative tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:gap-1.5 tw:rounded-lg tw:border tw:border-slate-200 tw:bg-white tw:p-2 tw:text-slate-800 tw:transition tw:hover:border-slate-300 tw:hover:bg-slate-50"
                aria-label="Cambiar de teatro"
                :aria-expanded="open ? 'true' : 'false'"
                data-testid="header-teatro-toggle"
                @click="toggle"
            >
                <svg
                    class="admin-header-icon admin-teatro-pin tw:inline-block tw:shrink-0"
                    width="17"
                    height="17"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <path
                        class="admin-teatro-pin-body"
                        fill="currentColor"
                        d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"
                    />
                    <circle
                        class="admin-teatro-pin-circle"
                        cx="12"
                        cy="10"
                        r="3"
                        fill="#fff"
                        stroke="none"
                    />
                </svg>
                <i
                    class="admin-teatro-chevron fas fa-chevron-down tw:shrink-0 tw:text-[9px] tw:text-slate-500 tw:mt-0.5"
                    :class="open ? 'tw:rotate-180' : ''"
                    aria-hidden="true"
                ></i>
            </button>

            <div
                v-show="open"
                class="tw:fixed tw:left-4 tw:right-4 tw:top-[72px] tw:z-50 tw:w-auto tw:max-w-none tw:rounded-xl tw:border tw:border-slate-200 tw:bg-white tw:shadow-xl tw:text-slate-900 tw:md:absolute tw:md:left-auto tw:md:right-0 tw:md:top-full tw:md:mt-2 tw:md:w-72 tw:md:max-w-[calc(100vw-2rem)]"
                data-testid="header-teatro-panel"
            >
                <div class="tw:border-b tw:border-slate-100 tw:px-4 tw:py-3">
                    <span class="tw:text-sm tw:font-semibold">Cambiar de teatro</span>
                    <span v-if="current && current.nombre" class="tw:mt-0.5 tw:block tw:text-[11px] tw:text-slate-500">
                        Estás en {{ current.nombre }}
                    </span>
                </div>

                <div v-if="loading" class="tw:space-y-2 tw:p-3" data-testid="header-teatro-loading">
                    <SkeletonItem
                        v-for="n in 3"
                        :key="'teatro-skeleton-' + n"
                        height="tw:h-9"
                        customClass="tw:w-full tw:rounded-lg"
                    />
                </div>

                <div v-else class="tw:p-2">
                    <button
                        v-for="teatro in options"
                        :key="teatro.id"
                        type="button"
                        class="tw:flex tw:w-full tw:items-center tw:gap-2.5 tw:rounded-lg tw:px-3 tw:py-2 tw:text-left tw:text-xs tw:text-slate-700 tw:transition-colors tw:hover:bg-slate-50"
                        :data-testid="'header-teatro-option-' + teatro.id"
                        @click="select(teatro)"
                    >
                        <i class="fas fa-theater-masks tw:w-4 tw:shrink-0 tw:text-center tw:text-slate-400" aria-hidden="true"></i>
                        <span class="tw:flex-1 tw:truncate">{{ teatro.nombre }}</span>
                        <i class="fas fa-arrow-right tw:text-[10px] tw:shrink-0 tw:text-slate-400" aria-hidden="true"></i>
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
    import { ref } from 'vue';
    import { useAdminTeatroSelector } from '@/composables/admin/layout/useAdminTeatroSelector';
    import { useHeaderDropdown } from '@/composables/admin/layout/useHeaderDropdown';
    import { SkeletonItem } from '@/modules/admin/ui/skeleton';

    const { loading, current, options, hasOptions, selectTeatro } = useAdminTeatroSelector();

    const rootRef = ref(null);
    const { open, toggle, close } = useHeaderDropdown(rootRef, { hover: false });

    function select(teatro) {
        close();
        selectTeatro(teatro);
    }
</script>

<style scoped>
    /*
     * Pin: rebote 0 / -5 / 0 al hover y aparición del círculo blanco.
     * El pin va relleno en slate-800; el círculo es el hueco blanco.
     * Mide 17px (visible ~14px), un pelín por debajo de los 15px del
     * carrito y la campana, y no se comprime en headers angostos.
     */
    .admin-teatro-pin {
        transition: transform 0.25s ease;
    }

    .admin-teatro-toggle:hover .admin-teatro-pin {
        animation: adminPinBounce 0.5s ease-in-out;
    }

    .admin-teatro-pin-circle {
        transform-box: fill-box;
        transform-origin: center;
        transform: scale(1);
        opacity: 1;
        transition: opacity 0.2s ease, transform 0.2s ease;
    }

    .admin-teatro-toggle:hover .admin-teatro-pin-circle {
        animation:
            adminPinCircleFade 0.1s ease 0.3s,
            adminPinCircleScale 0.5s ease 0.3s;
    }

    .admin-teatro-chevron {
        transition: rotate 0.2s ease, transform 0.2s ease;
    }

    @keyframes adminPinBounce {
        0% {
            transform: translateY(0);
        }
        60% {
            transform: translateY(-5px);
        }
        100% {
            transform: translateY(0);
        }
    }

    @keyframes adminPinCircleFade {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    @keyframes adminPinCircleScale {
        from {
            transform: scale(0.4);
        }
        to {
            transform: scale(1);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .admin-teatro-toggle:hover .admin-teatro-pin {
            animation: none;
        }

        .admin-teatro-toggle:hover .admin-teatro-pin-circle {
            animation: none;
        }
    }
</style>
